/**
 * Policy Rules
 *
 * Ordered policy rules for authorization decisions.
 * Rules are evaluated in order; first match wins.
 *
 * Rule ordering strategy:
 * 1. Tier 1: Admin overrides (highest priority)
 * 2. Tier 2: Explicit denials (lock, published, frozen); then role-based edit allows (items.edit, process.edit)
 * 3. Tier 3: Assignee (shaping_owner_id)/facilitator/list-owner allows (DOCS_V4)
 * 4. Tier 4: Ownership-based allows
 * 5. Tier 5: Context-based denials (timing and other named conditions)
 * 6. Tier 6: Default allows; final deny fallbacks
 */

import type { PolicyRule, PolicyReason } from './types';
import {
  isAdmin,
  isOwner,
  isAssignee,
  isOwnerOrAssigneeOrWorkspaceEditor,
  hasWorkspaceEditorRole,
  isLocked,
  isFieldLocked,
  isPublished,
  isFinalized,
  isDraft,
  isSubmissionOpen,
  areContributionsFrozen,
} from './predicates';

// ============================================================================
// Rule Helpers
// ============================================================================

/**
 * Create a simple allow reason.
 */
const allowReason = (code: string, data?: Record<string, unknown>): PolicyReason[] => [
  { code, data },
];

/**
 * Create a simple deny reason.
 */
const denyReason = (code: string, data?: Record<string, unknown>): PolicyReason[] => [
  { code, data },
];

// ============================================================================
// Policy Rules (Ordered - First Match Wins)
// ============================================================================

export const POLICY_RULES: PolicyRule[] = [
  // ========================================================================
  // Tier 1: Admin Override (highest priority)
  // ========================================================================
  
  {
    id: 'admin-override',
    effect: 'allow',
    actions: ['*'],
    description: 'Platform admins can perform any action',
    when: (ctx) => isAdmin(ctx.actor),
    reasons: () => allowReason('ADMIN_OVERRIDE'),
  },
  {
    id: 'tenant-admin-scope-allow',
    effect: 'allow',
    actions: ['*'],
    description: 'Tenant admins can perform any action within their tenant scope',
    when: (ctx) => (ctx.actor as { isTenantAdmin?: boolean }).isTenantAdmin === true,
    reasons: () => allowReason('TENANT_ADMIN_SCOPE'),
  },
  {
    id: 'workspace-admin-scope-allow',
    effect: 'allow',
    actions: ['*'],
    description: 'Workspace admins can perform any action within their current workspace',
    when: (ctx) => {
      const a = ctx.actor as { isWorkspaceAdmin?: boolean; currentWorkspaceId?: string };
      const resWs = (ctx.resource as { workspaceId?: string }).workspaceId;
      return a.isWorkspaceAdmin === true && (resWs == null || resWs === a.currentWorkspaceId);
    },
    reasons: () => allowReason('WORKSPACE_ADMIN_SCOPE'),
  },

  // ========================================================================
  // Tier 2: Explicit Denials (locks, published, frozen)
  // ========================================================================
  
  {
    id: 'locked-resource-deny-edit',
    effect: 'deny',
    actions: ['items.edit.*', 'items.delete', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Locked items cannot be edited',
    when: (ctx) => isLocked(ctx.resource),
    reasons: (ctx) => denyReason('RESOURCE_LOCKED', {
      lockedBy: ctx.resource.lockState?.lockedBy,
      lockedAt: ctx.resource.lockState?.lockedAt,
      lockReason: ctx.resource.lockState?.lockReason,
    }),
  },
  
  {
    id: 'published-resource-deny-edit',
    effect: 'deny',
    actions: ['items.edit.*', 'items.delete'],
    description: 'Published resources cannot be edited',
    when: (ctx) => isPublished(ctx.resource),
    reasons: () => denyReason('ALREADY_PUBLISHED'),
  },
  
  {
    id: 'finalized-resource-deny-edit',
    effect: 'deny',
    actions: ['items.edit.*', 'items.delete', 'lists.edit.*', 'process.edit.*'],
    description: 'Finalized resources cannot be edited',
    when: (ctx) => isFinalized(ctx.resource),
    reasons: () => denyReason('ALREADY_FINALIZED'),
  },
  
  {
    id: 'frozen-contributions-deny',
    effect: 'deny',
    actions: ['items.create', 'items.edit.content'],
    description: 'Cannot create or edit content when contributions are frozen',
    when: (ctx) => areContributionsFrozen(ctx.context),
    reasons: () => denyReason('CONTRIBUTIONS_FROZEN'),
  },
  
  {
    id: 'field-locked-deny',
    effect: 'deny',
    actions: ['items.edit.status'],
    description: 'Cannot edit locked fields',
    when: (ctx) => isFieldLocked(ctx.resource, 'status'),
    reasons: () => denyReason('FIELD_LOCKED', { field: 'status' }),
  },

  // Context-based denials for items (before allow rules so owner/assignee/editor are restricted)
  {
    id: 'submissions-closed-deny-edit',
    effect: 'deny',
    actions: ['items.edit.content', 'items.create'],
    description: 'Cannot edit content when submission window is closed',
    when: (ctx) => !isSubmissionOpen(ctx.context) && !isAssignee(ctx.actor, ctx.resource),
    reasons: (ctx) => denyReason('SUBMISSION_WINDOW_CLOSED', { phase: ctx.context.phase }),
  },

  // ========================================================================
  // items.edit: workspace-first allows (evaluated before ownership)
  // ========================================================================

  {
    id: 'platform-admin-allow',
    effect: 'allow',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Platform admins can edit any item',
    when: (ctx) => (ctx.actor as { isPlatformAdmin?: boolean }).isPlatformAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'platform-admin-allow' }),
  },
  {
    id: 'tenant-admin-allow',
    effect: 'allow',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Tenant admins can edit any item',
    when: (ctx) => (ctx.actor as { isTenantAdmin?: boolean }).isTenantAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'tenant-admin-allow' }),
  },
  {
    id: 'workspace-admin-allow',
    effect: 'allow',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Workspace admins can edit any item in their workspace',
    when: (ctx) => (ctx.actor as { isWorkspaceAdmin?: boolean }).isWorkspaceAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'workspace-admin-allow' }),
  },
  {
    id: 'owner-edit-allow',
    effect: 'allow',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Creator can edit their own item',
    when: (ctx) => isOwner(ctx.actor, ctx.resource),
    reasons: () => allowReason('OWNER_EDIT_ALLOWED'),
  },
  {
    id: 'assignee-edit-items',
    effect: 'allow',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Assignee (the person assigned to the item) can edit items they are assigned to',
    when: (ctx) => isAssignee(ctx.actor, ctx.resource) && !isFinalized(ctx.resource),
    reasons: () => allowReason('ASSIGNEE_ACCESS'),
  },
  {
    id: 'assignee-delete-items',
    effect: 'allow',
    actions: ['items.delete'],
    resourceTypes: ['item'],
    description: 'Assignee (the person assigned to the item) can delete draft items they are assigned to',
    when: (ctx) => isAssignee(ctx.actor, ctx.resource) && isDraft(ctx.resource),
    reasons: () => allowReason('ASSIGNEE_ACCESS'),
  },
  {
    id: 'workspace-editor-allow',
    effect: 'allow',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Workspace editors/managers can edit items',
    when: (ctx) => hasWorkspaceEditorRole(ctx.actor),
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'workspace-editor-allow' }),
  },
  {
    id: 'capability-holder-edit-allow',
    effect: 'allow',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description:
      'Holding the items.edit capability is itself grounds to edit an item — reaching this rule means the capability check already passed',
    // The evaluator checks the scoped capability BEFORE any rule runs
    // (policyEvaluator: a failed check returns ruleId 'capability-check' with
    // MISSING_CAPABILITY). So every rule below the Tier-2 denials is reached
    // only by someone the workspace has already granted items.edit.
    //
    // Without this rule the allows were owner, assignee, and a hardcoded list of
    // six admin-scope role slugs (WORKSPACE_EDITOR_ROLES), and everyone else fell
    // through to items-edit-deny / NOT_OWNER. An Item Manager — the role whose
    // permission list is exactly items.create, items.edit, items.categorize,
    // items.manage_deduplication, items.request_clarity, items.approve_ai_provenance
    // — could therefore not classify any item they had not personally created,
    // which left every item unsized and the planning engine with no effort to read.
    //
    // This rule sits after the locked / published / finalized / frozen /
    // field-locked / submissions-closed denials, so it cannot open a closed item.
    when: () => true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'capability-holder-edit-allow' }),
  },

  // ========================================================================
  // process.edit: workspace-first allows (cycle resource; v2-proposal-config)
  // Order: admin/tenant/workspace allows, then owner; deny is fallback.
  // ========================================================================
  {
    id: 'process-edit-platform-admin-allow',
    effect: 'allow',
    actions: ['process.edit', 'process.edit.*'],
    resourceTypes: ['cycle'],
    description: 'Platform admins can edit any cycle',
    when: (ctx) => (ctx.actor as { isPlatformAdmin?: boolean }).isPlatformAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'process-edit-platform-admin-allow' }),
  },
  {
    id: 'process-edit-tenant-admin-allow',
    effect: 'allow',
    actions: ['process.edit', 'process.edit.*'],
    resourceTypes: ['cycle'],
    description: 'Tenant admins can edit any cycle',
    when: (ctx) => (ctx.actor as { isTenantAdmin?: boolean }).isTenantAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'process-edit-tenant-admin-allow' }),
  },
  {
    id: 'process-edit-workspace-admin-allow',
    effect: 'allow',
    actions: ['process.edit', 'process.edit.*'],
    resourceTypes: ['cycle'],
    description: 'Workspace admins can edit any cycle in their workspace',
    when: (ctx) => (ctx.actor as { isWorkspaceAdmin?: boolean }).isWorkspaceAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'process-edit-workspace-admin-allow' }),
  },
  {
    id: 'process-edit-workspace-editor-allow',
    effect: 'allow',
    actions: ['process.edit', 'process.edit.*'],
    resourceTypes: ['cycle'],
    description: 'Workspace editors/managers can edit cycles',
    when: (ctx) => hasWorkspaceEditorRole(ctx.actor),
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'process-edit-workspace-editor-allow' }),
  },
  {
    id: 'process-edit-owner-allow',
    effect: 'allow',
    actions: ['process.edit', 'process.edit.*'],
    resourceTypes: ['cycle'],
    description: 'Cycle creator/owner can edit their cycle',
    when: (ctx) => isOwner(ctx.actor, ctx.resource),
    reasons: () => allowReason('OWNER_EDIT_ALLOWED'),
  },

  // workflow.*: first check DB (Settings → Roles → role_permissions); then code fallbacks (admin/shaper). No YAML.
  {
    id: 'workflow-db-capability-allow',
    effect: 'allow',
    actions: ['workflow.create', 'workflow.edit', 'workflow.edit.*', 'workflow.archive', 'workflow.set_default'],
    resourceTypes: ['workflow'],
    description: 'Allow when role has the workflow permission from DB (Settings → Roles)',
    when: (ctx) => {
      const caps = (ctx.actor as { capabilityIds?: string[] }).capabilityIds;
      if (!caps?.length) return false;
      const key = ctx.action.key;
      const required =
        key === 'workflow.create' ? 'WORKFLOW_CREATE'
        : key === 'workflow.set_default' ? 'WORKFLOW_SET_DEFAULT'
        : key === 'workflow.archive' ? 'WORKFLOW_ARCHIVE'
        : key === 'workflow.edit' || key.startsWith('workflow.edit') ? 'WORKFLOW_EDIT'
        : null;
      return required ? caps.includes(required) : false;
    },
    reasons: () => allowReason('DB_CAPABILITY', { source: 'role_permissions' }),
  },
  {
    id: 'workflow-platform-admin-allow',
    effect: 'allow',
    actions: ['workflow.create', 'workflow.edit', 'workflow.edit.*', 'workflow.archive', 'workflow.set_default'],
    resourceTypes: ['workflow'],
    description: 'Platform admins can manage workflows',
    when: (ctx) => (ctx.actor as { isPlatformAdmin?: boolean }).isPlatformAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'workflow-platform-admin-allow' }),
  },
  {
    id: 'workflow-tenant-admin-allow',
    effect: 'allow',
    actions: ['workflow.create', 'workflow.edit', 'workflow.edit.*', 'workflow.archive', 'workflow.set_default'],
    resourceTypes: ['workflow'],
    description: 'Tenant admins can manage workflows',
    when: (ctx) => (ctx.actor as { isTenantAdmin?: boolean }).isTenantAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'workflow-tenant-admin-allow' }),
  },
  {
    id: 'workflow-workspace-admin-allow',
    effect: 'allow',
    actions: ['workflow.create', 'workflow.edit', 'workflow.edit.*', 'workflow.archive', 'workflow.set_default'],
    resourceTypes: ['workflow'],
    description: 'Workspace admins can manage workflows in their workspace',
    when: (ctx) => (ctx.actor as { isWorkspaceAdmin?: boolean }).isWorkspaceAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'workflow-workspace-admin-allow' }),
  },
  {
    id: 'workflow-workspace-editor-allow',
    effect: 'allow',
    actions: ['workflow.create', 'workflow.edit', 'workflow.edit.*', 'workflow.archive', 'workflow.set_default'],
    resourceTypes: ['workflow'],
    description: 'Workspace editors can manage workflows',
    when: (ctx) => hasWorkspaceEditorRole(ctx.actor),
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'workflow-workspace-editor-allow' }),
  },

  // track.set_official_result: same roles as process.edit on cycle (configurable from Settings)
  {
    id: 'track-official-result-platform-admin-allow',
    effect: 'allow',
    actions: ['track.set_official_result'],
    resourceTypes: ['cycle'],
    description: 'Platform admins can choose the track result',
    when: (ctx) => (ctx.actor as { isPlatformAdmin?: boolean }).isPlatformAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'track-official-result-platform-admin-allow' }),
  },
  {
    id: 'track-official-result-tenant-admin-allow',
    effect: 'allow',
    actions: ['track.set_official_result'],
    resourceTypes: ['cycle'],
    description: 'Tenant admins can choose the track result',
    when: (ctx) => (ctx.actor as { isTenantAdmin?: boolean }).isTenantAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'track-official-result-tenant-admin-allow' }),
  },
  {
    id: 'track-official-result-workspace-admin-allow',
    effect: 'allow',
    actions: ['track.set_official_result'],
    resourceTypes: ['cycle'],
    description: 'Workspace admins can choose the track result',
    when: (ctx) => (ctx.actor as { isWorkspaceAdmin?: boolean }).isWorkspaceAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'track-official-result-workspace-admin-allow' }),
  },
  {
    id: 'track-official-result-workspace-editor-allow',
    effect: 'allow',
    actions: ['track.set_official_result'],
    resourceTypes: ['cycle'],
    description: 'Workspace editors can choose the track result',
    when: (ctx) => hasWorkspaceEditorRole(ctx.actor),
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'track-official-result-workspace-editor-allow' }),
  },
  {
    id: 'track-official-result-owner-allow',
    effect: 'allow',
    actions: ['track.set_official_result'],
    resourceTypes: ['cycle'],
    description: 'Cycle owner can choose the track result',
    when: (ctx) => isOwner(ctx.actor, ctx.resource),
    reasons: () => allowReason('OWNER_EDIT_ALLOWED'),
  },

  // session.publish: same roles as process.publish (configurable from Settings; governs "Publish Results")
  {
    id: 'session-publish-platform-admin-allow',
    effect: 'allow',
    actions: ['session.publish'],
    resourceTypes: ['cycle'],
    description: 'Platform admins can publish sessions',
    when: (ctx) => (ctx.actor as { isPlatformAdmin?: boolean }).isPlatformAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'session-publish-platform-admin-allow' }),
  },
  {
    id: 'session-publish-tenant-admin-allow',
    effect: 'allow',
    actions: ['session.publish'],
    resourceTypes: ['cycle'],
    description: 'Tenant admins can publish sessions',
    when: (ctx) => (ctx.actor as { isTenantAdmin?: boolean }).isTenantAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'session-publish-tenant-admin-allow' }),
  },
  {
    id: 'session-publish-workspace-admin-allow',
    effect: 'allow',
    actions: ['session.publish'],
    resourceTypes: ['cycle'],
    description: 'Workspace admins can publish sessions',
    when: (ctx) => (ctx.actor as { isWorkspaceAdmin?: boolean }).isWorkspaceAdmin === true,
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'session-publish-workspace-admin-allow' }),
  },
  {
    id: 'session-publish-workspace-editor-allow',
    effect: 'allow',
    actions: ['session.publish'],
    resourceTypes: ['cycle'],
    description: 'Workspace editors can publish sessions',
    when: (ctx) => hasWorkspaceEditorRole(ctx.actor),
    reasons: () => allowReason('WORKSPACE_CAPABILITY', { ruleId: 'session-publish-workspace-editor-allow' }),
  },
  {
    id: 'session-publish-owner-allow',
    effect: 'allow',
    actions: ['session.publish'],
    resourceTypes: ['cycle'],
    description: 'Cycle owner can publish sessions',
    when: (ctx) => isOwner(ctx.actor, ctx.resource),
    reasons: () => allowReason('OWNER_EDIT_ALLOWED'),
  },

  // ========================================================================
  // Tier 4: Ownership-Based Allows
  // ========================================================================
  
  {
    id: 'owner-edit-own-content-open',
    effect: 'allow',
    actions: ['items.edit.content'],
    resourceTypes: ['item'],
    description: 'Owners can edit their own content while submissions are open',
    when: (ctx) => 
      isOwner(ctx.actor, ctx.resource) && 
      isSubmissionOpen(ctx.context) &&
      isDraft(ctx.resource),
    reasons: () => allowReason('OWNER_EDIT_ALLOWED'),
  },
  
  {
    id: 'owner-view-own',
    effect: 'allow',
    actions: ['items.view', 'lists.view', 'process.view'],
    description: 'Owners can always view their own resources',
    when: (ctx) => isOwner(ctx.actor, ctx.resource),
    reasons: () => allowReason('OWNER_EDIT_ALLOWED'),
  },
  
  {
    id: 'owner-delete-own-draft',
    effect: 'allow',
    actions: ['items.delete'],
    resourceTypes: ['item'],
    description: 'Owners can delete their own draft items',
    when: (ctx) => isOwner(ctx.actor, ctx.resource) && isDraft(ctx.resource),
    reasons: () => allowReason('OWNER_EDIT_ALLOWED'),
  },

  // ========================================================================
  // Tier 5: Default Allows (general access for capable users)
  // ========================================================================
  
  {
    id: 'default-view-allow',
    effect: 'allow',
    actions: ['*.view', 'items.view', 'lists.view', 'process.view', 'session.view', 'analytics.view'],
    description: 'Default allow for view actions (capability check already passed)',
    when: () => true,
    reasons: () => allowReason('DEFAULT_ALLOW'),
  },
  
  {
    id: 'default-create-allow',
    effect: 'allow',
    actions: ['items.create', 'lists.create', 'process.create'],
    description: 'Default allow for create actions when submissions are open',
    when: (ctx) => isSubmissionOpen(ctx.context) || ctx.context.submissionWindowOpen === undefined,
    reasons: () => allowReason('DEFAULT_ALLOW'),
  },
  {
    id: 'default-lists-edit-allow',
    effect: 'allow',
    actions: ['lists.edit', 'lists.edit.*'],
    description: 'Allow item-pool membership/edit when capability check passed',
    when: () => true,
    reasons: () => allowReason('DEFAULT_ALLOW'),
  },
  {
    id: 'default-triage-allow',
    effect: 'allow',
    actions: ['items.triage'],
    resourceTypes: ['item'],
    description: 'Default allow for triage when capability check passed',
    when: () => true,
    reasons: () => allowReason('DEFAULT_ALLOW'),
  },
  
  {
    id: 'default-participate-allow',
    effect: 'allow',
    actions: ['session.participate', 'session.vote', 'values.propose'],
    description: 'Default allow for participation actions',
    when: () => true,
    reasons: () => allowReason('DEFAULT_ALLOW'),
  },

  {
    id: 'default-pool-membership-allow',
    effect: 'allow',
    actions: ['cycle.pool.add', 'cycle.pool.remove', 'session.pool.add', 'session.pool.remove'],
    description: 'Allow pool add/remove when capability check passed',
    when: () => true,
    reasons: () => allowReason('DEFAULT_ALLOW'),
  },
  {
    id: 'default-prioritization-write-allow',
    effect: 'allow',
    actions: ['lists.move_cutline', 'process.prioritize'],
    description: 'Allow prioritization write actions when capability check passed',
    when: () => true,
    reasons: () => allowReason('DEFAULT_ALLOW'),
  },

  // ========================================================================
  // Catch-all: Deny items.edit only when no allow rule matched
  // ========================================================================
  
  {
    id: 'items-edit-deny',
    effect: 'deny',
    actions: ['items.edit.*', 'items.edit'],
    resourceTypes: ['item'],
    description: 'Deny items.edit when no allow rule matched (workspace capability or owner/assignee)',
    when: () => true,
    reasons: () => denyReason('NOT_OWNER'),
  },
  {
    id: 'not-owner-deny',
    effect: 'deny',
    actions: ['items.delete'],
    resourceTypes: ['item'],
    description: 'Only owner, resource assignee (the person assigned to the item), or workspace editor can delete items (DOCS_V4)',
    when: (ctx) => !isOwnerOrAssigneeOrWorkspaceEditor(ctx.actor, ctx.resource),
    reasons: () => denyReason('NOT_OWNER'),
  },
];

// ============================================================================
// Rule Lookup Helpers
// ============================================================================

/**
 * Get a rule by ID.
 */
export function getRuleById(ruleId: string): PolicyRule | undefined {
  return POLICY_RULES.find(rule => rule.id === ruleId);
}

/**
 * Get all rules that apply to a specific action.
 */
export function getRulesForAction(action: string): PolicyRule[] {
  return POLICY_RULES.filter(rule => 
    rule.actions.some(pattern => {
      if (pattern === '*') return true;
      if (pattern.endsWith('.*')) {
        const prefix = pattern.slice(0, -2);
        return action === prefix || action.startsWith(prefix + '.');
      }
      return action === pattern;
    })
  );
}

/**
 * Get rule statistics (for debugging/admin).
 */
export function getRuleStats(): {
  total: number;
  allowRules: number;
  denyRules: number;
  byResourceType: Record<string, number>;
} {
  const stats = {
    total: POLICY_RULES.length,
    allowRules: POLICY_RULES.filter(r => r.effect === 'allow').length,
    denyRules: POLICY_RULES.filter(r => r.effect === 'deny').length,
    byResourceType: {} as Record<string, number>,
  };
  
  for (const rule of POLICY_RULES) {
    if (rule.resourceTypes) {
      for (const type of rule.resourceTypes) {
        stats.byResourceType[type] = (stats.byResourceType[type] || 0) + 1;
      }
    }
  }
  
  return stats;
}
